import {
  SourceEntityMigrationType,
  type ISourceEntity,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISO_DATE_FORMAT,
  Timestamp,
  TypeGuard,
  getError,
  toTimestamp,
  type Timezone,
} from '@principle-theorem/shared';
import { compact, flow, isNumber, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { ChartableSurface } from '@principle-theorem/principle-core/interfaces';
import {
  convertDateToTimestampFn,
  convertKeysToCamelCaseFn,
  convertNullToUndefinedFn,
} from '../../../source/source-helpers';
import { OasisNoteDataTransformer } from '../lib/oasis-notes-data-transformer';

export const PATIENT_NOTE_RESOURCE_TYPE = 'patientNotes';

export const PATIENT_NOTE_SOURCE_ENTITY: ISourceEntity = SourceEntity.init({
  metadata: {
    label: 'Patient Note List',
    description: '',
    idPrefix: PATIENT_NOTE_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export interface IPTPATNTS {
  // Patient Notes
  PATNUMBER: unknown; // Patient ID
  SEQNUMBER: unknown; //
  INITIALS: unknown; //
  DATE: unknown; //
  DATECREATED: unknown; //
  DETAILS: unknown; //
  NOTESTYPE: unknown; //
  STRUCKOUT: unknown; //
  HIGHLIGHT: unknown; //
  TOPRINT: unknown; //
  DATEAMENDED: unknown; //
  DOCTORNUMBER: unknown; //
  TOOTHNUMBER: unknown; //
  ONHOLD: unknown; //
  ALGORITHM: unknown; //
  COLORNUMBER: unknown; //
  COLORNUMBER2: unknown; //
  PICTURENUMBER: unknown; //
  ENTRYTIME: unknown; //
  BRANCHNUMBER: unknown; // Practice ID
  IMAGETYPE: unknown; //
  FORMNUMBER: unknown; //
  FORMTYPE: unknown; //
  AUDIONUMBER: unknown; //
  GRAPHICSNUMBER: unknown; //
  CATEGORY: unknown; //
  VIEWFLAG: unknown; //
  DIAGNOSISNUMBER: unknown; //
  SN01: unknown; //
  SN02: unknown; //
  SN03: unknown; //
  SN04: unknown; //
  SN05: unknown; //
  SN06: unknown; //
  SN07: unknown; //
  SN08: unknown; //
  SN09: unknown; //
}

interface IOasisPatientNoteMetadataRow {
  metadata1?: string;
  metadata2?: string;
  metadata3?: string;
  metadata4?: string;
  metadata5?: string;
  metadata6?: string;
  metadata7?: string;
  metadata8?: string;
  metadata9?: string;
}

export interface IOasisPatientNote {
  id: number;
  noteId: number;
  noteType?: number;
  createdAt: string;
  createdAtTime: string;
  updatedAt: string;
  initials?: string;
  patientId: number;
  practitionerId?: number;
  toothNumber?: number;
  categoryId?: number;
  categoryName?: string;
  note: string;
  serviceCode?: string;
  surfaces?: ChartableSurface[];
  metadata?: string[];
}

export function isOasisPatientNote(
  item: unknown,
  logError: boolean
): item is IOasisPatientNote {
  return TypeGuard.interface<IOasisPatientNote>(
    {
      id: isNumber,
      noteId: isNumber,
      noteType: TypeGuard.nilOr(isNumber),
      createdAt: isString,
      createdAtTime: isString,
      updatedAt: isString,
      initials: TypeGuard.nilOr(isString),
      patientId: isNumber,
      practitionerId: TypeGuard.nilOr(isNumber),
      toothNumber: TypeGuard.nilOr(isNumber),
      categoryId: TypeGuard.nilOr(isNumber),
      categoryName: TypeGuard.nilOr(isString),
      note: isString,
      serviceCode: TypeGuard.nilOr(isString),
      surfaces: TypeGuard.nilOr(
        TypeGuard.arrayOf(TypeGuard.enumValue(ChartableSurface))
      ),
      metadata: TypeGuard.nilOr(TypeGuard.arrayOf(isString)),
    },
    logError
  )(item);
}

export interface IOasisPatientNoteTranslations {
  createdAt: Timestamp;
}

export interface IOasisPatientNoteFilters {
  patientId: number;
}

const PATIENT_NOTE_SOURCE_QUERY = `
SELECT
	DISTINCT(note.note_id) AS id,
	category.category_name,
	note.*
FROM (
	SELECT
		SEQNUMBER AS note_id,
		NOTESTYPE AS note_type,

    normalise_oasis_date_field(DATE) AS created_at,
		ENTRYTIME AS created_at_time,
		normalise_oasis_date_field(DATEAMENDED) AS updated_at,
		convert_to_integer(PATNUMBER) AS patient_id,
		DOCTORNUMBER AS practitioner_id,
		convert_to_integer(NULLIF(CATEGORY, 0)) AS category_id,
		TOOTHNUMBER AS tooth_number,
		REPLACE(DETAILS, '��', '\r\n') AS note,
    sn01 AS metadata_1,
    sn02 AS metadata_2,
    sn03 AS metadata_3,
    sn04 AS metadata_4,
    sn05 AS metadata_5,
    sn06 AS metadata_6,
    sn07 AS metadata_7,
    sn08 AS metadata_8,
    sn09 AS metadata_9
	FROM PTPATNTS
) AS note
LEFT JOIN (
	SELECT
		convert_to_integer(REGEXP_REPLACE(SKEY, 'CLNCATGSE', '')) AS category_id,
		F1 AS category_name
	FROM SYTBLENT
  WHERE SKEY LIKE 'CLNCATGSE%'
) AS category
ON category.category_id = note.category_id
WHERE note.patient_id IS NOT NULL
ORDER BY id
`;

const PATIENT_NOTE_SOURCE_QUERY_V2 = `
SELECT
  SEQNUMBER AS id,
  SEQNUMBER AS note_id,
  NOTESTYPE AS note_type,

  normalise_oasis_date_field(DATE) AS created_at,

  ENTRYTIME AS created_at_time,
  normalise_oasis_date_field(DATEAMENDED) AS updated_at,
  convert_to_integer(PATNUMBER) AS patient_id,
  DOCTORNUMBER AS practitioner_id,
  TOOTHNUMBER AS tooth_number,
  REPLACE(DETAILS, '��', '\r\n') AS note,
  sn01 AS metadata_1,
  sn02 AS metadata_2,
  sn03 AS metadata_3,
  sn04 AS metadata_4,
  sn05 AS metadata_5,
  sn06 AS metadata_6,
  sn07 AS metadata_7,
  sn08 AS metadata_8,
  sn09 AS metadata_9
FROM PTPATNTS
WHERE PATNUMBER IS NOT NULL
ORDER BY id
`;

export type OasisPatientNote = ISourceEntityTypes<
  IOasisPatientNote,
  IOasisPatientNoteTranslations,
  IOasisPatientNoteFilters
>;

export class PatientNoteSourceEntity extends BaseSourceEntity<OasisPatientNote> {
  sourceEntity = PATIENT_NOTE_SOURCE_ENTITY;
  entityResourceType = PATIENT_NOTE_RESOURCE_TYPE;
  sourceQuery = [PATIENT_NOTE_SOURCE_QUERY, PATIENT_NOTE_SOURCE_QUERY_V2];
  verifySourceFn = isOasisPatientNote;
  allowOffsetJob = true;
  override transformDataFn = flow([
    convertKeysToCamelCaseFn(),
    convertNullToUndefinedFn(),
    convertDateToTimestampFn(),
    transformPatientNoteResults,
  ]);

  translate(
    data: IOasisPatientNote,
    timezone: Timezone
  ): IOasisPatientNoteTranslations {
    return {
      createdAt: toTimestamp(
        moment.tz(
          `${data.createdAt} ${data.createdAtTime}`,
          `${ISO_DATE_FORMAT} h:mm:ss a`,
          timezone
        )
      ),
    };
  }

  getSourceRecordId(data: IOasisPatientNote): number {
    return data.id;
  }

  getSourceLabel(data: IOasisPatientNote): string {
    return `${data.id}-${data.createdAt}`;
  }

  getFilterData(
    data: IOasisPatientNote,
    _timezone: Timezone
  ): IOasisPatientNoteFilters {
    return {
      patientId: data.patientId,
    };
  }
}

function transformPatientNoteResults(
  data: (Omit<IOasisPatientNote, 'serviceCode' | 'surfaces'> &
    IOasisPatientNoteMetadataRow)[]
): IOasisPatientNote[] {
  return data.map((item) => {
    const metadata = compact([
      item.metadata1,
      item.metadata2,
      item.metadata3,
      item.metadata4,
      item.metadata5,
      item.metadata6,
      item.metadata7,
      item.metadata8,
      item.metadata9,
    ]);

    item.metadata = metadata;

    try {
      const surfaceData = OasisNoteDataTransformer.getSurfaceDataFromOasisNote(
        item.note
      );

      return surfaceData
        ? {
            ...item,
            ...surfaceData,
          }
        : item;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        `Error transforming patient note data for item ${item.id}: ${getError(
          error
        )}`
      );
      return item;
    }
  });
}
